import React, { useEffect, useMemo, useState } from "react";
import { Button, Loader, MultiSelect, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { TaxRate } from "../../types/taxRates";
import CreateTaxRateModal from "./CreateTaxRateModal";
import {
  formatTaxRatePercent,
  isTaxRateCurrent,
} from "../../utils/taxRateFormat";

interface Props {
  /**
   * Current tax_rate IDs linked to the product/service being edited.
   * Shape: integer PKs from tax_rates (not UUIDs).
   */
  value: number[];
  /** Updates the parent's local state with the new selection. */
  onChange: (nextValue: number[]) => void;
  /** Optional helper text shown under the MultiSelect. */
  description?: string;
  /** Label override; defaults to "Applicable Tax Rates". */
  label?: string;
  disabled?: boolean;
  /**
   * Show a "Create new tax rate" link that opens CreateTaxRateModal. The new
   * rate is added to the options and auto-selected. Off by default so existing
   * edit flows are unchanged.
   */
  allowCreate?: boolean;
}

/**
 * Reusable Mantine MultiSelect for picking tax rates on a product or service
 * edit form. Fetches the salon's active tax rates on mount via AppContext and
 * filters out any rate that has already been end-dated (effective_end_date
 * in the past) so operators don't accidentally attach stale rates.
 *
 * The value prop is the list of integer tax_rate_id PKs. The parent is
 * expected to call AppContext.setProductTaxRates / setSalonServiceTaxRates
 * with the final array when it saves (or, when creating a product, send the
 * ids as `tax_rate_ids` on POST /products).
 */
export default function TaxRateMultiSelect({
  value,
  onChange,
  description,
  label = "Applicable Tax Rates",
  disabled = false,
  allowCreate = false,
}: Props) {
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };
  const { getTaxRates } = useApp();

  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      if (!businessInfo?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await getTaxRates(businessInfo.id);
        if (!cancelled) {
          setTaxRates(Array.isArray(response) ? response : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error("Failed to load tax rates:", err);
          setError("Failed to load tax rates");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetch();

    return () => {
      cancelled = true;
    };
  }, [businessInfo?.id, getTaxRates]);

  const options = useMemo(() => {
    const today = new Date();
    return taxRates
      .filter((rate) => isTaxRateCurrent(rate, today))
      .map((rate) => ({
        // Mantine MultiSelect values must be strings
        value: String(rate.id),
        label: `${rate.name} — ${formatTaxRatePercent(rate.rate)}`,
      }));
  }, [taxRates]);

  const stringValue = useMemo(() => value.map((v) => String(v)), [value]);

  const handleChange = (next: string[]) => {
    onChange(next.map((s) => parseInt(s, 10)).filter((n) => !isNaN(n)));
  };

  const handleCreated = (rate: TaxRate) => {
    setTaxRates((prev) => [...prev, rate]);
    onChange([...value, rate.id]);
  };

  if (isLoading) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Loader size="xs" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="red">
          {error}
        </Text>
      </Stack>
    );
  }

  const multiSelect = (
    <MultiSelect
      label={label}
      description={
        description ||
        "Tax rates that automatically apply to this item. Manage rates under Settings → Taxes."
      }
      data={options}
      value={stringValue}
      onChange={handleChange}
      searchable
      clearable
      disabled={disabled}
      nothingFoundMessage={
        allowCreate
          ? "No matching tax rates. Create one below."
          : "No tax rates configured"
      }
    />
  );

  if (!allowCreate) return multiSelect;

  return (
    <Stack gap={6}>
      {multiSelect}
      <Button
        variant="subtle"
        color="teal"
        size="sm"
        w="fit-content"
        px="xs"
        leftSection={<IconPlus size={16} />}
        onClick={() => setIsCreateOpen(true)}
        disabled={disabled}
      >
        Create new tax rate
      </Button>
      <CreateTaxRateModal
        opened={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleCreated}
      />
    </Stack>
  );
}
